<script setup lang="ts">
  import { computed, ref } from "vue";
  import type { HTMLAttributes } from "vue";
  import { useVModel } from "@vueuse/core";
  import { cn } from "@/lib/utils";
  import { MiniRichEditor } from "@/components/episto-ui/mini-rich-editor";
  import type { VariableItem } from "@/components/episto-ui/mini-rich-editor/variable-mention";
  import AiButton from "./AiButton.vue";
  import AiReviewArea from "./AiReviewArea.vue";
  import { useAiTextInput } from "./useAiTextInput";
  import type { AiGenerator } from "./types";

  const props = withDefaults(
    defineProps<{
      modelValue?: string;
      placeholder?: string;
      disabled?: boolean;
      mentionItems?: VariableItem[] | null;
      generate?: AiGenerator;
      class?: HTMLAttributes["class"];
    }>(),
    {
      modelValue: "",
      placeholder: "Enter text...",
      disabled: false,
      mentionItems: null,
      generate: undefined,
      class: undefined,
    },
  );

  const emit = defineEmits<{ "update:modelValue": [value: string] }>();

  const localValue = useVModel(props, "modelValue", emit, { passive: true });

  const {
    mode,
    readonly,
    displayedValue,
    showOriginal,
    onSelect,
    retry,
    accept,
    cancel,
    toggleOriginal,
  } = useAiTextInput({ value: localValue, generate: props.generate, format: "html" });

  const pickerOpen = ref(false);
  const generating = computed(() => mode.value === "generating");
  const empty = computed(() => (localValue.value ?? "").replace(/<[^>]*>/g, "").trim() === "");

  const onInput = (value: string): void => {
    if (!readonly.value) localValue.value = value;
  };
</script>

<template>
  <div :class="cn('w-full', props.class)">
    <MiniRichEditor
      :model-value="displayedValue"
      :placeholder="placeholder"
      :disabled="disabled"
      :readonly="readonly"
      :mention-items="mentionItems"
      @update:model-value="onInput">
      <template #toolbar-end>
        <AiButton
          v-model:open="pickerOpen"
          :disabled="generating || disabled"
          :empty="empty"
          @select="onSelect" />
      </template>
    </MiniRichEditor>

    <AiReviewArea
      class="mt-2"
      :mode="mode"
      :show-original="showOriginal"
      @accept="accept"
      @toggle="toggleOriginal"
      @retry="retry"
      @cancel="cancel" />
  </div>
</template>
